import type { MessageKey } from '../i18n/messages.en.ts';
import type { UserRole } from './types';

/**
 * MVP-224: the one place `UserRole.type` string literals are compared anywhere in web/ — every
 * other caller goes through these predicates instead of switching on `.type` itself. Each takes
 * `role | null | undefined` directly (rather than requiring the caller to null-check first)
 * because `null` is a normal, expected value here: signed out, `whoami()` hasn't resolved yet, or
 * the endpoint isn't deployed on this server (see `api/auth.ts`'s `whoami()` doc comment) — all of
 * which must read as "no", never throw.
 */

/** True for the roles allowed to use the Library's Download button.
 *
 * MVP-308: unlike the rest of this file's predicates, this one is no longer *only* a UI
 * affordance — `GET /api/songs/:documentId/download` (`strapi-template/src/bootstrap/roles.js`'s
 * `DOWNLOAD_ACTIONS`) enforces the identical boundary server-side, recording the event and
 * signing the actual file. Hiding the button for every other role is still worth doing (there is
 * no reason to show a control that will 403), but the real security boundary now lives on that
 * route, not here — see `DownloadLink.tsx`'s own doc comment for what is, and is not, still true
 * about the underlying `song.media.url` itself. */
export function canDownload(role: UserRole | null | undefined): boolean {
  return role?.type === 'admin' || role?.type === 'downloader';
}

/**
 * MVP-459: true for `downloader` only — the catalogue card's audition button, which plays the
 * song's short preview clip WITH SOUND.
 *
 * Narrower than `canDownload` on purpose, even though a downloader is the other role that holds
 * one. Everyone else already has a way to hear a song: an admin or a KJ can put it on the Stage.
 * A downloader cannot — `canOperate` is false for them, so Cockpit is not on their shelf at all —
 * and they are the one role whose whole job is deciding which file to pull. So this is not "the
 * roles allowed to listen", it is "the role that has no other way to", and widening it would put
 * a second, worse player next to the real one on the operator's screen.
 *
 * Pure affordance, and not even a hidden door: `song.preview.url` is the same public CDN URL
 * everyone's silent hover preview already loads (see `DownloadLink`'s doc comment on what an
 * unsigned Spaces URL is and is not). Nothing is gated here that the backend also enforces —
 * `DOWNLOADER_ACTIONS` already grants the search that hands the URL over.
 */
export function canAudition(role: UserRole | null | undefined): boolean {
  return role?.type === 'downloader';
}

/** True for `admin` only — the ingest screen (MVP-225) and, later, the user/activity screens. */
export function canIngest(role: UserRole | null | undefined): boolean {
  return role?.type === 'admin';
}

/**
 * MVP-354: true for `admin` only — the Library's Delete button.
 *
 * Same answer as `canIngest` today, and deliberately a separate predicate anyway: these two gate
 * different backend grants (`INGEST_ACTIONS` vs `CATALOG_DELETE_ACTIONS`), and the moment either
 * moves, the one that moved should be the one that changes here. Collapsing them would make a
 * future "operators may delete after all" decision look like a change to the ingest screen.
 *
 * Like `canDownload`, this is more than an affordance: `POST /api/catalog/songs/:id/delete` is
 * granted to admin only server-side, so hiding the button for everyone else is a way of not
 * offering a door to a wall — the refusal itself lives on the route.
 */
export function canDeleteSongs(role: UserRole | null | undefined): boolean {
  return role?.type === 'admin';
}

/**
 * True for the roles that run a karaoke session: the Cockpit and Settings screens.
 *
 * `downloader` is read-only catalog access and nothing else — `DOWNLOADER_ACTIONS` in
 * `strapi-template/src/bootstrap/roles.js` grants search/suggest, `find`/`findOne` on song,
 * media-file and artist, and enough auth to stay signed in. No queue, no rotation, no session, no
 * settings. So for a downloader every request those two screens make is a 403, and showing them a
 * way in is showing them a door to a wall.
 *
 * **`null` is allowed on purpose, unlike `canIngest`.** `whoami()` degrades to `null` on any
 * failure, including a backend that predates the endpoint (see `api/auth.ts`), and it can stay
 * `null` forever. Refusing the Cockpit on an unresolved role would take the app's main screen away
 * from an operator whose server is merely old — a far worse failure than a downloader seeing two
 * extra icons for the ~100 ms before `whoami()` answers. This is a UI affordance, not a security
 * boundary: the backend refuses the requests either way.
 *
 * MVP-585 added `rights` to the exclusion for the same "door to a wall" reason as `downloader`, and
 * more so: a rights agent holds none of `AUTHENTICATED_ACTIONS`, so every call the Cockpit makes is
 * a 403 for them. This predicate is written as a deny-list, so a role added to `UserRoleType`
 * without a line here silently becomes an operator — that is why the new type gets one.
 */
export function canOperate(role: UserRole | null | undefined): boolean {
  return role?.type !== 'downloader' && role?.type !== 'public' && role?.type !== 'rights';
}

/**
 * MVP-464: true for the roles that may hand out invite links — `admin` for the whole deployment,
 * `shop` for its own staff only.
 *
 * This is the rail/tab gate for the Accounts screen. The real boundary is two separate things on
 * the server: `STAFF_INVITE_ACTIONS` in `strapi-template/src/bootstrap/roles.js` decides who may
 * call the endpoint at all, and `ISSUABLE_ROLE_TYPES` in the invite controller decides which
 * `roleType` each issuer may actually mint — a shop holding `invite.create` could otherwise mint
 * itself an admin. `issuableRoles()` in `features/admin/invites.ts` mirrors that second table so
 * the form offers only what the server will accept.
 */
export function canInviteStaff(role: UserRole | null | undefined): boolean {
  return role?.type === 'admin' || role?.type === 'shop';
}

/**
 * MVP-464: true for `admin` only — the account list, its window edits and its role changes
 * (`ACCOUNT_ADMIN_ACTIONS`).
 *
 * Deliberately not `canInviteStaff`, and deliberately not `canIngest` either, even though it
 * answers the same as the latter today. A shop reaches the Accounts screen to issue a link and
 * must not see the deployment's whole roster there; and `/api/accounts` is a different grant from
 * the ingest screen's, so whichever one moves later should be the one that changes here.
 */
export function canManageAccounts(role: UserRole | null | undefined): boolean {
  return role?.type === 'admin';
}

/**
 * MVP-510 slice 4: true for `admin` and `shop` — the staff roster (`GET /api/accounts/staff` and
 * `PATCH /api/accounts/staff/:id`), which is the accounts of ONE venue's own people.
 *
 * Its own predicate, and not `canInviteStaff`, for the reason `canManageAccounts` above gives one
 * route over: the two mirror different grants (`STAFF_ACCOUNT_ACTIONS` vs `STAFF_INVITE_ACTIONS` in
 * `strapi-template/src/bootstrap/roles.js`), and whichever moves later should be the only one that
 * changes here. And not `canManageAccounts` either, which is the whole DEPLOYMENT's roster — the
 * difference between the two screens is the row-level venue filter in `account.listStaff`, so a
 * shop that answered true to that predicate would be asking for a list it is refused.
 *
 * `admin` is in it because the backend route is granted to `admin` as well — `listStaff`/`patchStaff`
 * are written out as literals in `ACCOUNT_ADMIN_ACTIONS` — and `canManageRooms` records what happens when a predicate mirrors only
 * part of a grant. An admin never sees the staff tab anyway — `canManageAccounts` is true for them,
 * and the deployment-wide roster is a superset of any one venue's — but that is the SCREEN's choice
 * of which tab to render, not this predicate's business.
 */
export function canManageStaff(role: UserRole | null | undefined): boolean {
  return role?.type === 'admin' || role?.type === 'shop';
}

/**
 * MVP-508: true for `admin` and `shop` — the Rooms screen, where a venue adds, renames, closes and
 * reopens its own rooms.
 *
 * The real boundary is `ROOM_WRITE_ACTIONS` in `strapi-template/src/bootstrap/roles.js`
 * (`api::room.room.create` and `.update`, and deliberately no `delete`). MVP-507 moved that pair to
 * `shop`; `admin` holds it too and always did, through `ACCOUNT_ADMIN_ACTIONS` spreading
 * `ROOM_ADMIN_ACTIONS` — that file says so in as many words ("`admin` is unchanged").
 *
 * This shipped for a moment as `shop` only, on the reasoning that the deployment owner administers
 * rooms through Strapi's own admin panel. Measured on a real `admin` login, that hides the rail
 * entry from the one person most likely to open this screen, while the backend would have answered
 * every one of its calls. It is a narrower re-run of the failure MVP-507 exists to fix — read the
 * docblock above `ROOM_ADMIN_ACTIONS` in `roles.js`: "that is why the deployment owner could not
 * find room management from their own login." A predicate that mirrors a grant has to mirror all of
 * it.
 *
 * A KJ holds `ROOM_READ_ACTIONS` alone, so for them every control on that screen would be a 403 —
 * `authenticated` stays off for the same reason the Cockpit is hidden from a downloader: a door to
 * a wall.
 *
 * Deliberately its own predicate even though it answers exactly what `canInviteStaff` answers today
 * — the reasoning `canManageAccounts` above sets out, one content type over: this gate names
 * `ROOM_WRITE_ACTIONS` and that one names `STAFF_INVITE_ACTIONS`, and whichever grant moves later
 * should be the only one that changes here.
 */
export function canManageRooms(role: UserRole | null | undefined): boolean {
  return role?.type === 'admin' || role?.type === 'shop';
}

/**
 * MVP-498 slice 2: true for every role that holds the grant — the ย้ายห้อง control that walks a whole
 * night into another room.
 *
 * The real boundary is `api::session.session.moveRoom` in `strapi-template/src/bootstrap/roles.js`,
 * and it sits on `AUTHENTICATED_ACTIONS`, NOT on `ROOM_WRITE_ACTIONS`. That placement is the owner's
 * decision and the reason this is not `canManageRooms`: moving a group to the VIP room mid-night is
 * the person at the console doing their job, while `ROOM_WRITE_ACTIONS` is the back office deciding
 * what rooms exist at all.
 *
 * FOUR roles, not three. `authenticated` holds it directly; `shop` inherits it through `SHOP_ACTIONS`
 * spreading `AUTHENTICATED_ACTIONS`; `admin` through `ADMIN_ACTIONS` doing the same; and `agency`
 * through `AGENCY_ACTIONS = [...AUTHENTICATED_ACTIONS]`, which is that role's entire definition. The
 * fourth is easy to miss because an agency is a sales rep with no staff and no group to walk
 * anywhere — but MVP-530's own note on `AGENCY_ACTIONS` records that an agency given a venue has
 * rooms and nights of its own, so the affordance is not a lie for them either.
 *
 * ALL OF THEM, and that is the whole point of writing a separate predicate rather than reusing one.
 * `canManageRooms` directly above records what happened the last time a predicate mirrored only part
 * of a grant: it shipped as `shop`-only, which hid the screen from the deployment owner while the
 * backend answered every call it made. A predicate that mirrors a grant has to mirror all of it —
 * including the role that got there by a spread nobody was looking at.
 *
 * `null` is false, unlike `canOperate`. An unresolved role here costs a KJ one hidden button on a
 * screen they can still run the night from, not the screen itself — and the server refuses the call
 * either way, so this is an affordance, not the boundary.
 */
export function canMoveSessionRoom(role: UserRole | null | undefined): boolean {
  return (
    role?.type === 'admin' || role?.type === 'shop' || role?.type === 'authenticated' || role?.type === 'agency'
  );
}

/**
 * MVP-642: true for the same four roles as `canMoveSessionRoom` directly above, and for the same
 * reason rather than by coincidence — `api::session.session.rotateJoinCode` sits on
 * `AUTHENTICATED_ACTIONS` in `strapi-template/src/bootstrap/roles.js`, two lines ABOVE
 * `api::session.session.moveRoom` on the very same list. So `authenticated` holds it directly,
 * `shop` through `SHOP_ACTIONS`'s spread, `admin` through `ADMIN_ACTIONS`'s, and `agency` because
 * `AGENCY_ACTIONS = [...AUTHENTICATED_ACTIONS]` is that role's whole definition.
 *
 * Written as its own predicate rather than aliasing `canMoveSessionRoom`, even though the two return
 * the same booleans today. They mirror two INDEPENDENT grants: the owner can move either one off
 * `AUTHENTICATED_ACTIONS` without touching the other, and an alias would silently keep answering for
 * the grant that moved. The lists agreeing is a fact to be re-checked against `roles.js`, not a
 * relationship to be encoded.
 *
 * Rotating is the KJ's own remedy for a leaked code, which is why the grant is where it is: the
 * person holding the room is the one who knows the card walked off, and making them phone the owner
 * to invalidate it is how a leaked code stays live all night.
 *
 * `null` is false, like `canMoveSessionRoom` — an unresolved role hides one control on a screen the
 * KJ can still run the night from, and the server refuses the call regardless.
 */
export function canRotateJoinCode(role: UserRole | null | undefined): boolean {
  return (
    role?.type === 'admin' || role?.type === 'shop' || role?.type === 'authenticated' || role?.type === 'agency'
  );
}

/**
 * MVP-509: true for `admin` alone — the Licences screen, the vendor's own roster of the customers
 * they have sold a package to and the branches each runs.
 *
 * The real boundary is two grants, and both are admin-only in `strapi-template/src/bootstrap/
 * roles.js`: `LICENCE_ADMIN_ACTIONS` (`licence.find`/`findOne`/`create`/`update`, no `delete`) and
 * `VENUE_ADMIN_ACTIONS` (the same four on `venue`, again no `delete`). The venue controller enforces
 * the write half a second time in code, refusing every other role with `VENUE_ADMIN_ONLY` — its own
 * docblock calls that the belt to the permission row's braces, because a permission row is data and
 * the admin panel can tick a box between boots.
 *
 * Deliberately NOT `canManageRooms`, which is the screen one level down and answers `shop` as well.
 * How many branches a customer may open is a property of the package they bought, so it is the
 * VENDOR's decision and never the shop's — that is the owner's decision of 2026-09-16, recorded at
 * the top of the venue controller. Deliberately not `canIngest` either, even though it answers the
 * same today, for the reason `canDeleteSongs` gives: these gate different grants, and whichever one
 * moves later should be the only one that changes here.
 */
export function canManageLicences(role: UserRole | null | undefined): boolean {
  return role?.type === 'admin';
}

/**
 * MVP-513: true for the audience the licence countdown is FOR — the venue and whoever is standing at
 * its machine — and false for `admin`, the vendor.
 *
 * Two audiences, two messages, and this predicate is the line between them. The venue is being told
 * their service is ending and who to call, because they cannot fix it themselves; the vendor is
 * being told a customer is lapsing and CAN act, which is an entirely different sentence and belongs
 * on the Licences screen (its own ticket), not on a strip across the top of every screen an admin
 * opens. An admin account resolves to no licence at all today, so this mostly guards the case where
 * one is ever attached to a vendor login — cheap, and the alternative is an admin being nagged daily
 * about a date only they can change.
 *
 * The guest is not in this decision at all: no guest surface ever receives a licence, and
 * `licenceExpiryBar.test.ts` pins that. A guest is not party to the contract.
 */
/**
 * MVP-278: true for `shop` alone — the plans page and the expiry strip's ต่ออายุ button. Mirrors
 * `PAYMENT_SHOP_ACTIONS` in `strapi-template/src/bootstrap/roles.js`, which grants `payment.create`
 * to `shop` and to nobody else: a KJ does not pay for the shop, and a rep's licence is not theirs to
 * renew. `null` is false: a button that 403s is worse than one that appears a moment late.
 */
export function canPayForPlan(role: UserRole | null | undefined): boolean {
  return role?.type === 'shop';
}

/**
 * MVP-278: true for `admin` alone — the slip queue (`PAYMENT_ADMIN_ACTIONS`: approve/reject). Its
 * own predicate rather than `canIngest`, for the reason `canManageLicences` gives: different grants,
 * and whichever moves later should be the only one that changes here.
 */
export function canReviewPayments(role: UserRole | null | undefined): boolean {
  return role?.type === 'admin';
}

export function showsLicenceCountdown(role: UserRole | null | undefined): boolean {
  return role?.type !== 'admin';
}

/**
 * MVP-585: true for `rights` and for NOTHING else — the rights-agent report, which is totals across
 * shops and never which shop played what.
 *
 * **Every other predicate in this file ORs in `admin`. This one deliberately does not**, and the
 * reason is not that an admin is untrusted — an admin can already read more than this screen shows,
 * by every route there is. It is that the rights surface is a strictly NARROWER view than admin's,
 * and the only thing that makes it narrow is that its reader holds no venue-scoped grant at all
 * (`RIGHTS_ACTIONS` in `api/rightsActions.ts` intersects `AUTHENTICATED_ACTIONS` in the empty set,
 * and `roles.allowlist.test.ts` asserts exactly that). Admitting `admin` would mean the screen is
 * sometimes rendered for an identity that also holds every venue read, which makes the disjointness
 * assertion a statement about a set nobody is confined to — true, and about nothing. Keeping this to
 * one role keeps "what a reader of this screen can see" answerable by reading one allowlist.
 *
 * `null` is false, like `canIngest` and unlike `canOperate`: an unresolved role costs a rights agent
 * one screen they can reload into, not a screen they run a night from.
 */
export function canReadRightsReport(role: UserRole | null | undefined): boolean {
  return role?.type === 'rights';
}

/**
 * MVP-657: who may correct a song's guide-vocal channel from the Cockpit. `admin` alone.
 *
 * This is an AFFORDANCE, not a security boundary, and the distinction is written here because the
 * two other admin-only gates nearby are the opposite. `api::song.song.update` and
 * `api::catalog.catalog.patchSongOverrides` both sit in `AUTHENTICATED_ACTIONS`
 * (`strapi-template/src/bootstrap/roles.js`), so every operator role can already make this write
 * through the API; hiding the control decides who is OFFERED it, and the server would accept the
 * same request from a KJ. Narrowing those grants is a deliberate separate decision — several other
 * things ride on `song.update` — and this predicate is where it would be reflected if it happened.
 *
 * Why admin rather than every KJ, when a KJ is the person who can HEAR the mistake: the value is
 * not a preference for tonight. It is written to the file's own record, it wins over the scanner
 * for ever (`overrides`), and it changes what the catalogue tells every other venue on this
 * deployment. A per-night pick already exists beside it — the L/R/Both segments — and costs nothing
 * if it is wrong.
 *
 * `null` is false, like `canIngest`: an unresolved role gets the control on the next render, and
 * offering an editor to an account whose role has not arrived is how a KJ discovers a button that
 * 403s.
 */
export function canTagGuideVocal(role: UserRole | null | undefined): boolean {
  return role?.type === 'admin';
}

/** The dictionary entry naming a role type. Strapi's built-in `authenticated` role is this
 * product's CUSTOMER role (the operator/KJ account a venue signs in with) — "Authenticated" is
 * Strapi's plumbing word for it and means nothing to the person reading the screen, so it is never
 * shown.
 *
 * MVP-272 Phase 7: the three invitable roles point at `admin.role.*`, the same entries the invite
 * form and the audit log read, so a role cannot be called one thing here and another there. */
export function roleLabelKey(role: UserRole | null | undefined): MessageKey {
  switch (role?.type) {
    case 'admin':
      return 'admin.role.admin';
    case 'downloader':
      return 'admin.role.downloader';
    case 'authenticated':
      return 'admin.role.customer';
    case 'shop':
      return 'admin.role.shop';
    case 'agency':
      return 'admin.role.agency';
    case 'rights':
      return 'admin.role.rights';
    case 'public':
      return 'account.role.guest';
    default:
      return 'account.role.signedOut';
  }
}
